import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { Trophy, Star, Crown, ExternalLink, Award, Shield, Users, BarChart } from 'lucide-react';

const Reviews = () => {
  const reviews = [
    {
      id: 'pub',
      name: 'PUB Casino',
      url: 'https://www.pubcasino.co.uk/',
      rating: 8.5,
      summary: 'A beloved UK casino brand that brings the authentic pub atmosphere to online gaming with generous welcome bonuses and extensive game selection.',
      keyHighlights: ['Authentic UK gaming experience', '100% welcome bonus up to £100', '500+ casino games', 'Community-focused approach'],
      pros: ['Authentic UK gaming atmosphere', 'Generous 100% welcome bonus', 'Extensive game library', 'Fair play guarantee', 'Reliable customer support'],
      cons: ['Limited live casino options', 'Mobile app could be enhanced', 'Bonus terms require attention'],
      verdict: 'PUB Casino perfectly blends authentic British pub culture with modern online gaming, offering genuine entertainment and community values that resonate with UK players.'
    },
    {
      id: 'nrg',
      name: 'NRG',
      url: 'https://nrg.bet/',
      rating: 8.7,
      summary: 'An energetic gaming platform that combines high-energy entertainment with substantial rewards, featuring 200% bonus and 100 free spins.',
      keyHighlights: ['High-energy gaming experience', '200% first deposit bonus', '100 free spins', 'Vibrant entertainment focus'],
      pros: ['Exceptional welcome package', 'High-energy gaming atmosphere', 'Generous bonus percentages', 'Free spins included', 'Modern gaming experience'],
      cons: ['May be too energetic for some players', 'Limited traditional casino focus', 'Bonus wagering requirements'],
      verdict: 'NRG delivers explosive gaming entertainment with one of the most generous welcome packages available, perfect for players seeking high-energy casino experiences.'
    },
    {
      id: 'mrplay',
      name: 'Mr. Play',
      url: 'https://www.mrplay.com/',
      rating: 8.9,
      summary: 'A sophisticated gaming destination that combines elegance with excitement, offering 100 spins and 100% bonus up to £200.',
      keyHighlights: ['Sophisticated gaming experience', '100 free spins', '100% bonus up to £200', 'Premium entertainment focus'],
      pros: ['Outstanding welcome package', 'Sophisticated platform design', 'High bonus limits', 'Free spins included', 'Quality gaming experience'],
      cons: ['May be too premium-focused for casual players', 'Complex bonus terms', 'Limited budget options'],
      verdict: 'Mr. Play represents the pinnacle of sophisticated online gaming, delivering premium experiences with exceptional bonuses that cater to discerning players.'
    }
  ];

  const getRatingColor = (rating) => {
    if (rating >= 9.0) return 'text-green-600';
    if (rating >= 8.5) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getRatingBg = (rating) => {
    if (rating >= 9.0) return 'bg-green-100';
    if (rating >= 8.5) return 'bg-yellow-100';
    return 'bg-orange-100';
  };

  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
              Comprehensive Casino Reviews
            </h1>
            <p className="text-xl text-book-brown-dark max-w-4xl mx-auto leading-relaxed">
              In-depth analysis of premium gaming platforms, combining rigorous testing, performance metrics,
              and real-world player experiences to deliver authoritative evaluations that inform your gaming decisions.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {reviews.map((review, index) => (
              <div key={review.id} className="bg-white rounded-xl shadow-book overflow-hidden hover:shadow-lg transition-all duration-200">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-book-brown">{review.name}</h3>
                    <div className={`px-3 py-1 rounded-full ${getRatingBg(review.rating)}`}>
                      <span className={`font-bold ${getRatingColor(review.rating)}`}>{review.rating}/10</span>
                    </div>
                  </div>

                  <p className="text-book-brown-dark mb-6 leading-relaxed">
                    {review.summary}
                  </p>

                  <div className="mb-6">
                    <h4 className="font-semibold text-book-brown mb-3">Key Highlights:</h4>
                    <ul className="space-y-1">
                      {review.keyHighlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-center text-sm text-book-brown-dark">
                          <div className="w-1.5 h-1.5 bg-book-brown rounded-full mr-2 flex-shrink-0"></div>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1 text-book-brown-light">
                      <Award className="h-4 w-4" />
                      <span className="text-sm font-medium">Expert Review</span>
                    </div>
                    <Link
                      to={`/reviews/${review.id}`}
                      data-discover="true"
                      className="inline-flex items-center space-x-2 bg-book-brown text-book-cream px-4 py-2 rounded-lg hover:bg-book-brown-light transition-colors duration-200 font-medium"
                    >
                      <span>Read Full Review</span>
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-book-beige rounded-xl p-8 shadow-book-inset mb-12">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Our Review Methodology</h2>
            <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
              <p className="mb-4">
                Every casino review published on Brit Ember represents the culmination of comprehensive evaluation
                methodology that combines quantitative analysis, qualitative assessment, and real-world testing.
                Our systematic approach ensures that every recommendation delivers genuine value to discerning players.
              </p>
              <p className="mb-4">
                The review process begins with extensive platform analysis, examining licensing credentials,
                regulatory compliance, and operational infrastructure. We conduct thorough testing of payment
                systems, customer support quality, and platform stability to identify any potential issues
                before they impact players.
              </p>
              <p className="mb-4">
                Performance metrics form the foundation of our evaluations, with detailed analysis of payout
                speeds, game fairness, and platform reliability. We track actual withdrawal times, test customer
                support response quality, and monitor platform uptime to provide accurate performance data.
              </p>
              <p className="mb-4">
                User experience evaluation goes beyond surface-level impressions, incorporating accessibility
                testing, mobile performance analysis, and cross-device compatibility assessments. We examine
                how platforms perform under real-world conditions, including peak usage periods and network
                variability.
              </p>
              <p className="mb-4">
                Security and responsible gaming measures receive particular attention, with detailed analysis
                of player protection tools, financial safeguards, and self-exclusion options. We verify the
                effectiveness of these measures through comprehensive testing and validation procedures.
              </p>
              <p>
                Our commitment to transparency means every review clearly explains our methodology, highlights
                both strengths and limitations, and provides specific recommendations based on different player
                profiles. We maintain independence from casino operators, ensuring our evaluations serve player
                interests exclusively. This rigorous, methodical approach transforms subjective opinions into
                actionable intelligence that empowers informed gaming decisions.
              </p>
            </div>
          </div>

          <div className="text-center">
            <div className="bg-gradient-to-r from-book-brown to-book-brown-light rounded-xl p-8 text-book-cream">
              <h3 className="text-2xl font-bold mb-4">Expert Analysis, Player-Focused</h3>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
                Our comprehensive reviews combine technical expertise with practical insights,
                helping you identify platforms that match your gaming preferences and deliver exceptional experiences.
              </p>
              <p className="text-sm opacity-80">
                All reviews are updated regularly to reflect current platform performance and industry developments.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default Reviews;
